import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb, HeadphonesIcon, X } from 'lucide-react';
import { PlatformFeedbackPanel } from './PlatformFeedbackPanel';
import { SupportChatPanel } from './SupportChatPanel';

type SupportTab = 'feedback' | 'support';

/** Opent de widget op een tabblad van buitenaf (bv. vanuit het zoekvenster). */
export const openSupportWidget = (tab: SupportTab) =>
  window.dispatchEvent(new CustomEvent<SupportTab>('belagom:support-open', { detail: tab }));

/**
 * Zwevende support-widget rechtsonder. Vervangt de menu-items
 * "Verbeterpunten Platform" en "Chat Klantenservice" in Mijn Belagom, zodat de
 * zijbalk alleen nog bedrijfsmatige compliance-onderdelen bevat en de
 * professional overal op het platform feedback of hulp kan vragen.
 */
export const PlatformSupportWidget: React.FC<{ zoekKnop?: React.ReactNode }> = ({ zoekKnop }) => {
  const { t } = useTranslation('belagom');
  const [openTab, setOpenTab] = useState<SupportTab | null>(null);

  useEffect(() => {
    const onOpen = (e: Event) => setOpenTab((e as CustomEvent<SupportTab>).detail);
    window.addEventListener('belagom:support-open', onOpen);
    return () => window.removeEventListener('belagom:support-open', onOpen);
  }, []);

  const tabCls = (tab: SupportTab) =>
    `flex-1 px-3 py-2 text-xs font-semibold rounded-lg transition-colors ${
      openTab === tab ? 'bg-white text-[#0B1F3A] shadow-sm' : 'text-white/80 hover:text-white'
    }`;

  return (
    <div data-zoek-negeer className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3 print:hidden">
      {openTab && (
        <div className="w-[min(24rem,calc(100vw-2.5rem))] h-[min(34rem,calc(100vh-8rem))] flex flex-col rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2.5 bg-[#0B1F3A]">
            <div className="flex-1 flex items-center gap-1 rounded-lg bg-white/10 p-1">
              <button type="button" className={tabCls('feedback')} onClick={() => setOpenTab('feedback')}>
                {t('widget.feedbackTab', { defaultValue: 'Feedback' })}
              </button>
              <button type="button" className={tabCls('support')} onClick={() => setOpenTab('support')}>
                {t('widget.supportTab', { defaultValue: 'Klantenservice' })}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setOpenTab(null)}
              aria-label={t('button.cancel', { defaultValue: 'Sluiten' })}
              className="p-1.5 rounded-lg text-white/80 hover:bg-white/10 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>

          <div className={`flex-1 min-h-0 ${openTab === 'feedback' ? 'overflow-y-auto p-4' : ''}`}>
            {openTab === 'feedback' ? <PlatformFeedbackPanel /> : <SupportChatPanel />}
          </div>
        </div>
      )}

      {!openTab && (
        <>
          <button
            type="button"
            onClick={() => setOpenTab('feedback')}
            className="group flex items-center gap-2 rounded-full bg-white border border-gray-200 shadow-lg pl-3 pr-4 py-2.5 text-sm font-semibold text-[#0B1F3A] hover:shadow-xl transition-shadow"
          >
            <Lightbulb size={16} className="text-yellow-500" />
            {t('widget.feedbackButton', { defaultValue: 'Feedback' })}
          </button>
          {/* Zoeken links naast klantenservice, beide onder de Feedback-knop */}
          <div className="flex items-center gap-3">
            {zoekKnop}
            <button
              type="button"
              onClick={() => setOpenTab('support')}
              aria-label={t('widget.supportButton', { defaultValue: 'Klantenservice' })}
              className="w-12 h-12 rounded-full bg-[#0B1F3A] text-white shadow-lg flex items-center justify-center hover:bg-[#1A3459] transition-colors"
            >
              <HeadphonesIcon size={20} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
